import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useState } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, UserPlus, Edit, Trash2, DollarSign, Shield, Calendar,
} from "lucide-react";

export const UsersSection = ({ users, fetchData }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ name: "", email: "", password: "", role: "user" });
  const [balanceDialogOpen, setBalanceDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [balanceAmount, setBalanceAmount] = useState("");
  const [balanceDescription, setBalanceDescription] = useState("");
  const [balanceTxnDate, setBalanceTxnDate] = useState("");
  const [backdateDialogOpen, setBackdateDialogOpen] = useState(false);
  const [backdateUser, setBackdateUser] = useState(null);
  const [backdateValue, setBackdateValue] = useState("");

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleCreateUser = async () => {
    try {
      const headers = getAuthHeaders();
      if (editingUser) {
        const updateData = { name: userForm.name, email: userForm.email, role: userForm.role };
        if (userForm.password) updateData.password = userForm.password;
        await axios.put(`${API}/admin/users/${editingUser.user_id}`, updateData, { headers, withCredentials: true });
        toast.success("User updated");
      } else {
        await axios.post(`${API}/admin/users`, userForm, { headers, withCredentials: true });
        toast.success("User created");
      }
      setUserDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save user");
    }
  };

  const handleEditUser = (u) => {
    setEditingUser(u);
    setUserForm({ name: u.name, email: u.email, password: "", role: u.role });
    setUserDialogOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm("Delete this user? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: getAuthHeaders(), withCredentials: true });
      toast.success("User deleted");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleUpdateBalance = async () => {
    if (!selectedUser || !balanceAmount) return;
    try {
      await axios.put(`${API}/admin/users/${selectedUser.user_id}/balance`, {
        amount: parseFloat(balanceAmount),
        description: balanceDescription || "Admin balance adjustment",
        transaction_date: balanceTxnDate ? new Date(balanceTxnDate).toISOString() : null
      }, { headers: getAuthHeaders(), withCredentials: true });
      toast.success("Balance updated with transaction record");
      setBalanceDialogOpen(false);
      setSelectedUser(null);
      setBalanceAmount("");
      setBalanceDescription("");
      setBalanceTxnDate("");
      fetchData();
    } catch (error) {
      toast.error("Failed to update balance");
    }
  };

  const handleBackdate = async () => {
    if (!backdateUser || !backdateValue) return;
    try {
      await axios.put(`${API}/admin/users/${backdateUser.user_id}/backdate`, { created_at: new Date(backdateValue).toISOString() }, { headers: getAuthHeaders(), withCredentials: true });
      toast.success("User creation date updated");
      setBackdateDialogOpen(false);
      setBackdateUser(null);
      setBackdateValue("");
      fetchData();
    } catch (error) {
      toast.error("Failed to update date");
    }
  };

  const resetForm = () => {
    setEditingUser(null);
    setUserForm({ name: "", email: "", password: "", role: "user" });
  };

  const filteredUsers = users.filter(
    (u) =>
      u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Card className="bg-white border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between border-b border-slate-100">
          <CardTitle className="text-slate-800 font-heading">User Management</CardTitle>
          <div className="flex items-center gap-3">
            <div className="relative w-56">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search users..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 border-slate-200 h-9 text-sm" data-testid="user-search-input" />
            </div>
            <Dialog open={userDialogOpen} onOpenChange={(open) => { setUserDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white gap-1.5" data-testid="create-user-btn">
                  <UserPlus className="w-4 h-4" /> Add User
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-slate-200 max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-slate-800 font-heading">{editingUser ? "Edit User" : "Create New User"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label>Full Name</Label>
                    <Input value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} placeholder="John Doe" data-testid="user-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} placeholder="user@example.com" data-testid="user-email-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>{editingUser ? "New Password (leave blank to keep)" : "Password"}</Label>
                    <Input type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} placeholder={editingUser ? "--------" : "Enter password"} data-testid="user-password-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Role</Label>
                    <select value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })} className="w-full h-10 px-3 border border-slate-200 rounded-md text-sm" data-testid="user-role-select">
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <Button onClick={handleCreateUser} className="w-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="submit-user-btn">
                    {editingUser ? "Update User" : "Create User"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-slate-500 text-xs uppercase tracking-wider bg-slate-50">
                  <th className="px-5 py-3 font-semibold">User</th>
                  <th className="px-5 py-3 font-semibold">Role</th>
                  <th className="px-5 py-3 font-semibold">Balance</th>
                  <th className="px-5 py-3 font-semibold">Referrals</th>
                  <th className="px-5 py-3 font-semibold">Joined</th>
                  <th className="px-5 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredUsers.map((u) => (
                  <tr key={u.user_id} className="hover:bg-slate-50">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${u.role === "admin" ? "bg-amber-100" : "bg-slate-100"}`}>
                          <span className={`font-semibold text-sm ${u.role === "admin" ? "text-amber-700" : "text-slate-600"}`}>
                            {u.name?.charAt(0)?.toUpperCase() || "U"}
                          </span>
                        </div>
                        <div>
                          <p className="text-slate-700 font-medium text-sm">{u.name}</p>
                          <p className="text-slate-400 text-xs">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.role === "admin" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                        {u.role === "admin" && <Shield className="w-3 h-3 inline mr-1" />}{u.role}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-slate-700 font-mono text-sm">${(u.balance || 0).toLocaleString()}</span>
                      {u.referral_balance > 0 && <span className="text-amber-500 text-xs block">+${u.referral_balance} ref</span>}
                    </td>
                    <td className="px-5 py-3 text-slate-600 text-sm">{u.total_referrals || 0}</td>
                    <td className="px-5 py-3 text-slate-500 text-sm">{new Date(u.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-3">
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-slate-500 hover:text-slate-700" onClick={() => handleEditUser(u)} data-testid={`edit-user-${u.user_id}`}><Edit className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-slate-500 hover:text-emerald-600" onClick={() => { setSelectedUser(u); setBalanceDialogOpen(true); }} data-testid={`adjust-balance-${u.user_id}`}><DollarSign className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-slate-500 hover:text-blue-600" onClick={() => { setBackdateUser(u); setBackdateValue(u.created_at ? u.created_at.split("T")[0] : ""); setBackdateDialogOpen(true); }} data-testid={`backdate-user-${u.user_id}`}><Calendar className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-400 hover:text-rose-600" onClick={() => handleDeleteUser(u.user_id)} data-testid={`delete-user-${u.user_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Balance Dialog */}
      <Dialog open={balanceDialogOpen} onOpenChange={setBalanceDialogOpen}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-800 font-heading">Adjust User Balance</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="p-4 bg-slate-50 rounded-xl">
              <p className="text-slate-500 text-sm">User: {selectedUser?.name}</p>
              <p className="text-slate-500 text-sm">Current Balance: ${(selectedUser?.balance || 0).toLocaleString()}</p>
            </div>
            <div className="space-y-2">
              <Label>Amount (use negative to subtract)</Label>
              <Input type="number" value={balanceAmount} onChange={(e) => setBalanceAmount(e.target.value)} placeholder="e.g., 100 or -50" data-testid="balance-amount-input" />
            </div>
            <div className="space-y-2">
              <Label>Description (for transaction record)</Label>
              <Input value={balanceDescription} onChange={(e) => setBalanceDescription(e.target.value)} placeholder="e.g., Deposit via wire transfer" data-testid="balance-desc-input" />
            </div>
            <div className="space-y-2">
              <Label>Transaction Date (optional, for backdating)</Label>
              <Input type="datetime-local" value={balanceTxnDate} onChange={(e) => setBalanceTxnDate(e.target.value)} data-testid="balance-date-input" />
              <p className="text-xs text-slate-500">Leave empty to use current date</p>
            </div>
            <Button onClick={handleUpdateBalance} className="w-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="submit-balance-btn">Update Balance & Create Transaction</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Backdate Dialog */}
      <Dialog open={backdateDialogOpen} onOpenChange={setBackdateDialogOpen}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-800 font-heading">Backdate User Creation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="p-4 bg-slate-50 rounded-xl">
              <p className="text-slate-500 text-sm">User: {backdateUser?.name}</p>
              <p className="text-slate-500 text-sm">Current Date: {backdateUser?.created_at ? new Date(backdateUser.created_at).toLocaleDateString() : "N/A"}</p>
            </div>
            <div className="space-y-2">
              <Label>New Creation Date</Label>
              <Input type="date" value={backdateValue} onChange={(e) => setBackdateValue(e.target.value)} data-testid="backdate-input" />
            </div>
            <Button onClick={handleBackdate} className="w-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="submit-backdate-btn">Update Date</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
